import React, { useCallback, useState } from "react"
import { LoaderCircleIcon, SaveIcon, XCircleIcon } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { PopoverClose } from "@radix-ui/react-popover"

export interface FeaturesConfig {
  evidence?: "enabled" | "disabled"
  annotations?: "enabled" | "disabled"
  ioa?: "enabled" | "disabled"
  evidenceCounts?: "enabled" | "disabled"
  tags?: "enabled" | "disabled"
  quarantine?: "enabled" | "disabled"
  transcript?: "enabled" | "disabled"
  [key: string]: string | undefined
}

interface Feature {
  key: keyof FeaturesConfig
  label: string
}

const features: Feature[] = [
  { key: "evidence", label: "Evidence" },
  { key: "annotations", label: "Annotations" },
  { key: "ioa", label: "IOA" },
  { key: "evidenceCounts", label: "Evidence Counts" },
  { key: "tags", label: "Tags" },
  { key: "quarantine", label: "Quarantine" },
  { key: "transcript", label: "Transcript" },
]

const DEFAULT_FEATURE_STATES: Required<
  Pick<FeaturesConfig, "evidence" | "annotations" | "ioa" | "evidenceCounts" | "tags" | "quarantine" | "transcript">
> = {
  evidence: "enabled",
  annotations: "enabled",
  ioa: "enabled",
  evidenceCounts: "enabled",
  tags: "enabled",
  quarantine: "enabled",
  transcript: "enabled",
}

export interface SharedFeaturesTabProps {
  config: FeaturesConfig | undefined
  onSave: (featureStates: FeaturesConfig) => Promise<void> | void
  onRemove: () => Promise<void> | void
  isFetching: boolean
  currentAction?: "SAVE_SECTION" | "REMOVE_SECTION" | null
}

export function SharedFeaturesTab({ config, onSave, onRemove, isFetching, currentAction }: SharedFeaturesTabProps) {
  const [featureStates, setFeatureStates] = useState<FeaturesConfig>(() => {
    const definedConfigEntries = Object.fromEntries(
      Object.entries(config ?? {}).filter(([, value]) => value !== undefined),
    )
    return { ...DEFAULT_FEATURE_STATES, ...definedConfigEntries }
  })

  const handleFeatureChange = useCallback((feature: keyof FeaturesConfig, value: "enabled" | "disabled") => {
    setFeatureStates((prev) => ({
      ...prev,
      [feature]: value,
    }))
  }, [])

  const handleSave = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      onSave(featureStates)
    },
    [onSave, featureStates],
  )

  const handleRemoveClick = useCallback(() => {
    onRemove()
  }, [onRemove])

  const showSaveLoading = isFetching && currentAction === "SAVE_SECTION"
  const showRemoveLoading = isFetching && currentAction === "REMOVE_SECTION"

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-full flex-col">
        <div className="flex-1 overflow-y-auto p-4">
          <div className="mx-auto w-full space-y-6">
            <div className="rounded-lg border border-border bg-card p-4 text-card-foreground">
              <h3 className="mb-4 text-lg font-medium text-card-foreground">Configure Features</h3>
              <div className="space-y-4">
                {features.map((feature, index) => (
                  <div
                    key={feature.key}
                    className="grid grid-cols-1 items-center gap-4 rounded-lg border border-border bg-primary-foreground p-4 md:grid-cols-[auto_1fr_auto]"
                  >
                    <div className="flex items-center gap-3">
                      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-info/15 font-mono text-sm font-medium text-info">
                        {index + 1}
                      </span>
                      <div
                        className={`h-3 w-3 rounded-full ${
                          featureStates[feature.key] === "enabled" ? "bg-green-500" : "bg-red-500"
                        }`}
                      />
                    </div>

                    <div className="min-w-0">
                      <h4 className="font-medium text-foreground">{feature.label}</h4>
                    </div>

                    <div className="flex items-center gap-2">
                      <Select
                        value={featureStates[feature.key]}
                        onValueChange={(value) => handleFeatureChange(feature.key, value as "enabled" | "disabled")}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="enabled">
                            <span className="flex items-center gap-2">
                              <span className="h-2 w-2 rounded-full bg-green-500" />
                              Enabled
                            </span>
                          </SelectItem>
                          <SelectItem value="disabled">
                            <span className="flex items-center gap-2">
                              <span className="h-2 w-2 rounded-full bg-red-500" />
                              Disabled
                            </span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={handleSave} className="flex-shrink-0 border-t border-border p-4">
          <div className="mx-auto flex w-full items-center gap-3">
            <Button type="submit" disabled={isFetching} className="flex-1 bg-info hover:bg-info/90">
              {showSaveLoading && <LoaderCircleIcon className="mr-2 h-4 w-4 animate-spin" />}
              <SaveIcon className="h-4 w-4" />
              Save Features
            </Button>

            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="default"
                  className="flex-1 gap-2 border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                  disabled={isFetching}
                >
                  {showRemoveLoading && <LoaderCircleIcon className="mr-2 h-4 w-4 animate-spin" />}
                  <XCircleIcon className="h-4 w-4" />
                  <span className="hidden sm:inline">Remove Section</span>
                  <span className="sm:hidden">Remove</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent side="top" className="w-80">
                <div className="space-y-3">
                  <p className="text-sm font-medium text-popover-foreground">Remove Features Configuration?</p>
                  <p className="text-sm text-muted-foreground">
                    This will remove all feature configurations and reset all features to their default enabled state.
                  </p>
                  <div className="flex items-center justify-end gap-3">
                    <PopoverClose asChild>
                      <Button type="button" variant="ghost" size="sm">
                        Cancel
                      </Button>
                    </PopoverClose>
                    <PopoverClose asChild>
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={handleRemoveClick}
                        disabled={isFetching}
                      >
                        Remove
                      </Button>
                    </PopoverClose>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </form>
      </div>
    </div>
  )
}
